import React from "react";
import {
    Check,
    ChevronLeft,
    ChevronRight,
    Download,
    FileJson,
    Pin,
    PinOff,
    Save,
    Trash2,
} from "lucide-react";
import {
    Excalidraw,
    MainMenu,
    WelcomeScreen,
    exportToBlob,
    serializeAsJSON,
} from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";

const downloadBlob = (
    blob,
    fileName
) => {
    const objectUrl =
        URL.createObjectURL(blob);

    const link =
        document.createElement("a");

    link.href = objectUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(objectUrl);
};

const makeSafeFileName = (value) =>
    String(value || "whiteboard")
        .trim()
        .replace(/[^a-z0-9]+/gi, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase() || "whiteboard";

const ChildWhiteboard = ({
    childId,
    scope = "dashboard",
    title = "Tabula",
    workspaceMode = "private",
    sharedWorkspace = null,
    onSharedDataChange,
    onSharedEditing,
}) => {
    const [initialData, setInitialData] =
        React.useState(null);

    const [saveStatus, setSaveStatus] =
        React.useState("Saved");

    const [
        isToolsOpen,
        setIsToolsOpen,
    ] = React.useState(true);

    const [
        areToolsPinned,
        setAreToolsPinned,
    ] = React.useState(true);

    const excalidrawRef = React.useRef(null);
    const appliedSceneRef = React.useRef("");
    const applyingRemoteRef = React.useRef(false);
    const sceneRef = React.useRef({
        elements: [],
        appState: {
            viewBackgroundColor:
                "#ffffff",
        },
        files: {},
    });

    const saveTimerRef =
        React.useRef(null);

    const storageKey =
        `shadowGroveWhiteboard_${childId}_${scope}`;

    const normalizeScene = (
        scene = {}
    ) => {
        const cleanAppState = {
            ...(scene.appState || {}),
        };

        delete cleanAppState.collaborators;

        return {
            elements: Array.isArray(
                scene.elements
            )
                ? scene.elements
                : [],
            appState: {
                ...cleanAppState,
                viewBackgroundColor:
                    cleanAppState
                        .viewBackgroundColor ||
                    "#ffffff",
            },
            files:
                scene.files &&
                typeof scene.files ===
                    "object"
                    ? scene.files
                    : {},
        };
    };

    React.useEffect(() => {
        // Keep the editor mounted while remote strokes arrive.

        if (
            workspaceMode === "shared"
        ) {
            const restoredScene =
                normalizeScene(
                    sharedWorkspace
                        ?.tabula_data || {}
                );

            const signature = JSON.stringify([restoredScene.elements, restoredScene.files]);
            if (signature === appliedSceneRef.current) return;
            appliedSceneRef.current = signature;
            sceneRef.current = restoredScene;
            if (excalidrawRef.current) {
                applyingRemoteRef.current = true;
                excalidrawRef.current.addFiles(Object.values(restoredScene.files));
                excalidrawRef.current.updateScene({elements: restoredScene.elements});
                queueMicrotask(() => { applyingRemoteRef.current = false; });
            } else setInitialData(restoredScene);

            setSaveStatus("Saved");

            return;
        }

        try {
            const savedScene =
                localStorage.getItem(
                    storageKey
                );

            if (savedScene) {
                const parsedScene =
                    JSON.parse(savedScene);

                const restoredScene =
                    normalizeScene(
                        parsedScene
                    );

                sceneRef.current =
                    restoredScene;

                setInitialData(
                    restoredScene
                );

                setSaveStatus("Saved");

                return;
            }
        } catch (error) {
            console.error(
                "Unable to load private whiteboard:",
                error
            );
        }

        const emptyScene =
            normalizeScene({});

        sceneRef.current =
            emptyScene;

        setInitialData(emptyScene);
        setSaveStatus("Saved");
    }, [
        storageKey,
        workspaceMode,
        sharedWorkspace?.workspace_id,
        sharedWorkspace?.tabula_data,
    ]);

    React.useEffect(() => {
        return () => {
            if (
                saveTimerRef.current
            ) {
                window.clearTimeout(
                    saveTimerRef.current
                );
            }
        };
    }, []);

    const saveWhiteboard =
        async () => {
            try {
                const {
                    elements,
                    appState,
                    files,
                } = sceneRef.current;

                const cleanAppState = {
                    ...(appState || {}),
                };

                delete cleanAppState
                    .collaborators;

                const sceneToSave = {
                    elements,
                    appState:
                        cleanAppState,
                    files,
                    updated_at:
                        new Date()
                            .toISOString(),
                };

                if (
                    workspaceMode ===
                    "shared"
                ) {
                    if (
                        !onSharedDataChange
                    ) {
                        throw new Error(
                            "Shared Tabula saving is not connected."
                        );
                    }

                    await onSharedDataChange(
                        sceneToSave
                    );
                } else {
                    localStorage.setItem(
                        storageKey,
                        JSON.stringify(
                            sceneToSave
                        )
                    );
                }

                setSaveStatus("Saved");
            } catch (error) {
                console.error(
                    "Unable to save whiteboard:",
                    error
                );

                setSaveStatus(
                    "Save failed"
                );
            }
        };

    const handleWhiteboardChange = (
        elements,
        appState,
        files
    ) => {
        const signature = JSON.stringify([elements, files]);
        if (workspaceMode === "shared" && (applyingRemoteRef.current || signature === appliedSceneRef.current)) return;
        appliedSceneRef.current = signature;
        if (workspaceMode === "shared") onSharedEditing?.();
        sceneRef.current = {
            elements: [
                ...elements,
            ],
            appState,
            files,
        };

        setSaveStatus("Saving");

        if (
            saveTimerRef.current
        ) {
            window.clearTimeout(
                saveTimerRef.current
            );
        }

        saveTimerRef.current =
            window.setTimeout(() => {
                void saveWhiteboard();
            }, 700);
    };

    const exportWhiteboardPng =
        async () => {
            try {
                const {
                    elements,
                    appState,
                    files,
                } = sceneRef.current;

                if (!elements.length) {
                    return;
                }

                const imageBlob =
                    await exportToBlob({
                        elements,
                        appState: {
                            ...appState,
                            exportBackground:
                                true,
                            exportWithDarkMode:
                                false,
                        },
                        files,
                        mimeType:
                            "image/png",
                        maxWidthOrHeight:
                            2400,
                        exportPadding: 24,
                    });

                downloadBlob(
                    imageBlob,
                    `${makeSafeFileName(
                        title
                    )}.png`
                );
            } catch (error) {
                console.error(
                    "Unable to export whiteboard image:",
                    error
                );
            }
        };

    const exportWhiteboardFile =
        () => {
            try {
                const {
                    elements,
                    appState,
                } = sceneRef.current;

                const sceneJson =
                    serializeAsJSON({
                        elements,
                        appState,
                    });

                const sceneBlob =
                    new Blob(
                        [sceneJson],
                        {
                            type: "application/json",
                        }
                    );

                downloadBlob(
                    sceneBlob,
                    `${makeSafeFileName(
                        title
                    )}.excalidraw`
                );
            } catch (error) {
                console.error(
                    "Unable to export editable whiteboard:",
                    error
                );
            }
        };

    if (!initialData) {
        return (
            <div className="flex min-h-[500px] items-center justify-center rounded-2xl border border-[#ffd166]/25 bg-[#080b14] text-sm text-[#b0b8d6]">
                Opening whiteboard...
            </div>
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#ffd166]/30 bg-[#080b14]">
           
   

            <div className="relative min-h-[520px] flex-1 overflow-auto bg-white">
        <div
    className={`
        z-30
        h-0
        w-0
        overflow-visible
        ${
            areToolsPinned
                ? "sticky left-2 top-14"
                : "absolute left-2 top-14"
        }
    `}
>
    {isToolsOpen ? (
        <aside
            className="
                flex
                w-[76px]
                flex-col
                gap-1
                rounded-xl
                border
                border-[#ffd166]/45
                bg-black/95
                p-1.5
                shadow-[0_0_20px_rgba(0,0,0,0.35)]
                backdrop-blur-sm
            "
        >
            <div className="flex items-center justify-between gap-1">
                <button
                    type="button"
                    onClick={() =>
                        setAreToolsPinned(
                            (currentValue) =>
                                !currentValue
                        )
                    }
                    title={
                        areToolsPinned
                            ? "Let tools scroll with Tabula"
                            : "Keep tools visible"
                    }
                    aria-label={
                        areToolsPinned
                            ? "Unpin tools"
                            : "Pin tools"
                    }
                    className="
                        inline-flex
                        h-6
                        w-6
                        items-center
                        justify-center
                        rounded-md
                        border
                        border-[#ffd166]/25
                        bg-[#2b1548]/60
                        text-[#fff3c4]
                        hover:border-[#ffd166]
                        hover:text-[#ffd166]
                    "
                >
                    {areToolsPinned ? (
                        <Pin size={11} />
                    ) : (
                        <PinOff size={11} />
                    )}
                </button>

                <button
                    type="button"
                    onClick={() =>
                        setIsToolsOpen(false)
                    }
                    title="Hide tools"
                    aria-label="Hide Tabula tools"
                    className="
                        inline-flex
                        h-6
                        w-6
                        items-center
                        justify-center
                        rounded-md
                        border
                        border-[#ffd166]/25
                        bg-[#2b1548]/60
                        text-[#fff3c4]
                        hover:border-[#ffd166]
                        hover:text-[#ffd166]
                    "
                >
                    <ChevronLeft size={12} />
                </button>
            </div>

            <button
                type="button"
                onClick={saveWhiteboard}
                className="
                    inline-flex
                    flex-col
                    items-center
                    justify-center
                    gap-0.5
                    rounded-lg
                    border
                    border-[#ffd166]/35
                    bg-[#2b1548]/70
                    px-1
                    py-1.5
                    text-[9px]
                    font-semibold
                    text-[#fff3c4]
                    hover:border-[#ffd166]
                    hover:text-[#ffd166]
                "
            >
                <Save size={12} />
                Save
            </button>

            <button
                type="button"
                onClick={exportWhiteboardPng}
                className="
                    inline-flex
                    flex-col
                    items-center
                    justify-center
                    gap-0.5
                    rounded-lg
                    border
                    border-[#ffd166]/30
                    bg-[#080b14]
                    px-1
                    py-1.5
                    text-[9px]
                    font-semibold
                    text-[#fff3c4]
                    hover:border-[#ffd166]
                    hover:text-[#ffd166]
                "
            >
                <Download size={12} />
                PNG
            </button>

            <button
                type="button"
                onClick={exportWhiteboardFile}
                className="
                    inline-flex
                    flex-col
                    items-center
                    justify-center
                    gap-0.5
                    rounded-lg
                    border
                    border-[#ffd166]/30
                    bg-[#080b14]
                    px-1
                    py-1.5
                    text-[9px]
                    font-semibold
                    text-[#fff3c4]
                    hover:border-[#ffd166]
                    hover:text-[#ffd166]
                "
            >
                <FileJson size={12} />
                File
            </button>
        </aside>
    ) : (
        <button
            type="button"
            onClick={() =>
                setIsToolsOpen(true)
            }
            title="Show Tabula tools"
            aria-label="Show Tabula tools"
            className="
                inline-flex
                h-8
                w-7
                items-center
                justify-center
                rounded-r-lg
                border
                border-l-0
                border-[#ffd166]/45
                bg-black/95
                text-[#ffd166]
                shadow-[0_0_16px_rgba(0,0,0,0.35)]
            "
        >
            <ChevronRight size={14} />
        </button>
    )}
</div>
<Excalidraw
    excalidrawAPI={api => { excalidrawRef.current = api; }}
    initialData={
        initialData
    }
    onChange={
        handleWhiteboardChange
    }
    theme="light"
    name={title}
>
    <MainMenu>
        <MainMenu.Group title="Liberi Curiosi">
            <MainMenu.Item
                onSelect={
                    saveWhiteboard
                }
            >
                Servare Tabulam
            </MainMenu.Item>

            <MainMenu.Item
                onSelect={
                    exportWhiteboardPng
                }
            >
                Exportare PNG
            </MainMenu.Item>

            <MainMenu.Item
                onSelect={
                    exportWhiteboardFile
                }
            >
                Archivum Tabulae
            </MainMenu.Item>
        </MainMenu.Group>
    </MainMenu>

 
</Excalidraw>
            </div>
        </div>
    );
};

export default ChildWhiteboard;